import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import FormDrawer from "src/components/Form/FormDrawer";
import ImagePreviewForUpload from "src/components/Form/imagePreview";
import { imageUrl } from "src/request/types";
import { updateSectorSaga } from "src/store/sectors/saga";
import { SectorDetailDto, SectorSliderDto } from "src/store/sectors/type";
import * as Yup from "yup";

const SectorEditForm = ({
  data,
  onHide,
  open,
  onSuccessClick,
}: {
  data: SectorDetailDto;
  onSuccessClick: (message: string) => void;
  onHide: () => void;
  open: boolean;
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  return (
    <React.Fragment>
      <FormDrawer
        drawerProps={{
          isOpen: open,
          onHide: onHide,
          title: t("Sector.EditFormTitle").toString(),
        }}
        onHide={onHide}
        loader
        initialValues={{
          Id: data.Id,
          Content: data.Content ?? "",
          ShortContent: data.ShortContent ?? "",
          ImageFile: "",
          ImageFileField: [data.Image ?? ""],
          ShowWeb: data.ShowWeb,
          Name: data.Name,
          SeoTitle: data.SeoTitle ?? "",
          SeoDescription: data.SeoDescription ?? "",
          SeoImageFile: "",
          SeoImageFileField: [data.SeoImage ?? ""],
          SectorSliders: data.SectorSliders ?? [],
          Images: [],
          Translates: data.Translates.map((x) => {
            return {
              LanguageId: x.LanguageId,
              Name: x.Name ?? "",
              Content: x.Content ?? "",
              ShortContent: x.ShortContent ?? "",
              Seo: x.Seo ?? "",
              SeoTitle: x.SeoTitle ?? "",
              SeoDescription: x.SeoDescription ?? "",
            };
          }),
        }}
        onSubmit={(values) => {
          dispatch(
            updateSectorSaga({
              payload: {
                onSuccess: (message, payload) => {
                  onHide();
                  onSuccessClick(message);
                },
                onError: (message) => {
                  toast.error(message, {
                    theme: "colored",
                  });
                },
              },
              id: data.Id,
              body: values,
            })
          );
        }}
        validationSchema={Yup.object().shape({
          Name: Yup.string().required(t("Validation.Required").toString()),
          Translates: Yup.array(
            Yup.object({
              Name: Yup.string().required(t("Validation.Required").toString()),
            })
          ),
        })}
        multilanguageinputs={[
          {
            field: "Name",
            label: t("Sector.Name").toString(),
          },
          {
            field: "ShortContent",
            label: t("Sector.ShortContent").toString(),
            isEditor: true,
          },
          {
            field: "Content",
            label: t("Sector.Content").toString(),
            isEditor: true,
          },
          {
            field: "SeoTitle",
            label: t("Sector.SeoTitle").toString(),
            type: "textarea",
          },
          {
            field: "SeoDescription",
            label: t("Sector.SeoDescription").toString(),
            type: "textarea",
          },
        ]}
        inputs={[
          {
            field: "ImageFile",
            label: t("Sector.ImageFile").toString(),
            type: "file",
            fileField: "ImageFileField",
            accept: "image/png, image/jpeg",

            fileValueRenderer: (values, setFieldValue) => {
              return (
                <div className="d-flex flex-wrap justify-content-start gap-2 mt-2">
                  {values.ImageFile ? (
                    <div>
                      <ImagePreviewForUpload
                        src={URL.createObjectURL(values.ImageFile)}
                        width={85}
                        height={45}
                      />
                    </div>
                  ) : (
                    <ImagePreviewForUpload
                      src={imageUrl + values.ImageFileField}
                      width={85}
                      height={45}
                    />
                  )}
                </div>
              );
            },
          },
          {
            field: "SeoImageFile",
            label: t("Sector.SeoImageFile").toString(),
            type: "file",
            fileField: "SeoImageFileField",
            accept: "image/png, image/jpeg",

            fileValueRenderer: (values, setFieldValue) => {
              return (
                <div className="d-flex flex-wrap justify-content-start gap-2 mt-2">
                  {values.SeoImageFile ? (
                    <div>
                      <ImagePreviewForUpload
                        src={URL.createObjectURL(values.SeoImageFile)}
                        width={85}
                        height={45}
                      />
                    </div>
                  ) : (
                    <ImagePreviewForUpload
                      src={imageUrl + values.SeoImageFileField}
                      width={85}
                      height={45}
                    />
                  )}
                </div>
              );
            },
          },
          {
            field: "Images",
            label: t("Sector.SliderImages").toString(),
            type: "file",
            multiple: true,
            overrideOnChange: (e, values, setFieldValue) => {
              setFieldValue("Images", [...values.Images, ...e]);
            },
            fileValueRenderer: (values, setFieldValue) => {
              return (
                <div className="d-flex flex-wrap justify-content-start gap-2 mt-2">
                  {values.Images.map((x: File, i: number) => {
                    var url = URL.createObjectURL(x);
                    return (
                      <div key={i}>
                        <ImagePreviewForUpload
                          src={url}
                          width={85}
                          height={45}
                          onDelete={() => {
                            setFieldValue &&
                              setFieldValue(
                                "Images",
                                values.Images.filter(
                                  (y: File) => URL.createObjectURL(y) !== url
                                )
                              );
                          }}
                        />
                      </div>
                    );
                  })}
                  {values.SectorSliders.map((x: SectorSliderDto) => (
                    <div key={x.Id}>
                      <ImagePreviewForUpload
                        src={imageUrl + x.Path}
                        width={85}
                        height={45}
                        onDelete={() => {
                          setFieldValue &&
                            setFieldValue(
                              "SectorSliders",
                              values.SectorSliders.filter(
                                (y: any) => y.Id !== x.Id
                              )
                            );
                        }}
                      />
                    </div>
                  ))}
                </div>
              );
            },
          },
          {
            field: "ShowWeb",
            label: t("Sector.ShowWeb").toString(),
            type: "switch",
          },
        ]}
      />
    </React.Fragment>
  );
};
export default SectorEditForm;
